// Ideeenbox / verbetervoorstellen — gedeelde DTOs + Zod (batch-10 #24).
//
// Mirror van `contact-requests.ts` (lichter): een medewerker dient een idee of
// verbetervoorstel in met een verplichte categorie. Het verzoek landt via
// IDEE_INGEDIEND in het KWALITEIT-postvak en is daar af te handelen.
//
// Eén bron van waarheid: dit schema wordt gebruikt door de API (`.safeParse`)
// EN door het portal-form (client-side validatie vóór verzenden), zodat het
// form nooit iets kan sturen dat de API weigert.

import { z } from 'zod'

// ─── Enums (gespiegeld vanuit Prisma) ────────────────────────────────────────

export const IdeeCategorieEnum = z.enum([
  'BEVEILIGING',
  'FUNCTIONALITEIT',
  'VERBETERING',
  'PLANNING',
  'DOCUMENTEN',
  'MATERIAAL',
])
export type IdeeCategorie = z.infer<typeof IdeeCategorieEnum>

export const IdeeStatusEnum = z.enum([
  'OPEN',
  'GELEZEN',
  'AFGEHANDELD',
  'GEANNULEERD',
  // FO-016 (batch-14) — expliciete behandel-lifecycle.
  'IN_BEHANDELING',
  'OVERGENOMEN',
  'AFGEWEZEN',
])
export type IdeeStatus = z.infer<typeof IdeeStatusEnum>

// FO-016 (batch-14) — inzending-type (idee / feedback / vraag).
export const IdeeTypeEnum = z.enum(['IDEE', 'FEEDBACK', 'VRAAG'])
export type IdeeType = z.infer<typeof IdeeTypeEnum>

// FO-016 (batch-14) — moderatie-status van een reactie op een openbaar voorstel.
export const IdeeReactieStatusEnum = z.enum(['IN_MODERATIE', 'GOEDGEKEURD', 'AFGEWEZEN'])
export type IdeeReactieStatus = z.infer<typeof IdeeReactieStatusEnum>

// ─── Input schemas ───────────────────────────────────────────────────────────

export const CreateIdeeSchema = z.object({
  // FO-016 — type is optioneel met default IDEE zodat bestaande clients (die
  // alleen categorie+omschrijving sturen) blijven werken.
  type: IdeeTypeEnum.default('IDEE'),
  titel: z
    .string()
    .trim()
    .min(3, 'Geef een korte titel')
    .max(120, 'Houd de titel onder de 120 tekens')
    .optional(),
  categorie: IdeeCategorieEnum,
  omschrijving: z
    .string()
    .trim()
    .min(5, 'Omschrijf je inzending in een paar woorden')
    .max(4000, 'Houd de omschrijving onder de 4000 tekens'),
})
export type CreateIdeeInput = z.infer<typeof CreateIdeeSchema>

export const CancelIdeeSchema = z.object({
  reden: z.string().trim().max(500).optional(),
})
export type CancelIdeeInput = z.infer<typeof CancelIdeeSchema>

export const CompleteIdeeSchema = z.object({
  interneNotitie: z.string().trim().max(2000).optional(),
})
export type CompleteIdeeInput = z.infer<typeof CompleteIdeeSchema>

export const IdeeInternalNoteSchema = z.object({
  notitie: z.string().trim().min(1).max(2000),
})
export type IdeeInternalNoteInput = z.infer<typeof IdeeInternalNoteSchema>

// ─── FO-016 (batch-14) — behandelaar: status + terugkoppeling ─────────────────

/** "In behandeling nemen" — optionele terugkoppeling naar de indiener. */
export const IdeeInBehandelingSchema = z.object({
  terugkoppeling: z.string().trim().max(2000).optional(),
})
export type IdeeInBehandelingInput = z.infer<typeof IdeeInBehandelingSchema>

/** Overnemen (positieve afronding) — terugkoppeling naar de indiener. */
export const IdeeOvernemenSchema = z.object({
  terugkoppeling: z.string().trim().max(2000).optional(),
})
export type IdeeOvernemenInput = z.infer<typeof IdeeOvernemenSchema>

/** Afwijzen — terugkoppeling met reden (geen zwart gat). */
export const IdeeAfwijzenSchema = z.object({
  terugkoppeling: z
    .string()
    .trim()
    .min(3, 'Geef een korte reden voor de indiener')
    .max(2000, 'Houd de terugkoppeling onder de 2000 tekens'),
})
export type IdeeAfwijzenInput = z.infer<typeof IdeeAfwijzenSchema>

// ─── FO-016 — publiceren als openbaar voorstel ────────────────────────────────

/**
 * Publiceren: de behandelaar mag de tekst redigeren op toon/spelling (de naam
 * blijft intern bekend bij behandelaar + teamleider). `vervaltOp` (ISO-datum,
 * Europe/Amsterdam op de UI-rand) archiveert het voorstel zodat de feed niet
 * vervuilt; leeg = geen vervaldatum.
 */
export const PubliceerIdeeSchema = z.object({
  titel: z
    .string()
    .trim()
    .min(3, 'Geef het voorstel een titel')
    .max(120, 'Houd de titel onder de 120 tekens'),
  tekst: z
    .string()
    .trim()
    .min(5, 'Het voorstel mag niet leeg zijn')
    .max(5000, 'Houd het voorstel onder de 5000 tekens'),
  vervaltOp: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Gebruik een geldige datum')
    .nullable()
    .optional(),
})
export type PubliceerIdeeInput = z.infer<typeof PubliceerIdeeSchema>

// ─── FO-016 — gemodereerde reacties op een openbaar voorstel ──────────────────

/** Een collega dient een reactie in op een openbaar voorstel (gaat naar moderatie). */
export const IdeeVoorstelReactieSchema = z.object({
  inhoud: z
    .string()
    .trim()
    .min(2, 'Schrijf een korte reactie')
    .max(2000, 'Houd de reactie onder de 2000 tekens'),
})
export type IdeeVoorstelReactieInput = z.infer<typeof IdeeVoorstelReactieSchema>

/** Teamleider modereert een ingediende reactie. */
export const ModereerReactieSchema = z.object({
  besluit: z.enum(['GOEDKEUREN', 'AFWIJZEN']),
  reden: z.string().trim().max(1000).optional(),
})
export type ModereerReactieInput = z.infer<typeof ModereerReactieSchema>

// ─── FO-016 — vraag doorzetten naar de hulpvraag-flow (ContactRequest) ────────

/**
 * Doelgroep waarheen een vraag-inzending wordt doorgezet. Subset van de
 * ContactRequest-types die voor een doorgezette ideeenbox-vraag logisch zijn
 * (geen CUSTOMER_CONTACT/CONFIDENTIAL_ADVISOR: die vragen om klant-/VP-context).
 */
export const DoorzetVraagDoelgroepEnum = z.enum(['TEAMLEADER', 'HR', 'KASPER'])
export type DoorzetVraagDoelgroep = z.infer<typeof DoorzetVraagDoelgroepEnum>

export const DoorzetVraagSchema = z.object({
  doelgroep: DoorzetVraagDoelgroepEnum,
  terugkoppeling: z.string().trim().max(2000).optional(),
})
export type DoorzetVraagInput = z.infer<typeof DoorzetVraagSchema>

// ─── DTOs ────────────────────────────────────────────────────────────────────

/** Eigen-view voor de indiener (portal). Geen interne notitie. */
export interface MijnIdeeDTO {
  id: string
  type: IdeeType
  titel: string | null
  categorie: IdeeCategorie
  omschrijving: string
  status: IdeeStatus
  // FO-016 — terugkoppeling die de behandelaar bij een statuswijziging schreef.
  terugkoppeling: string | null
  // Is deze inzending als openbaar voorstel gepubliceerd?
  gepubliceerd: boolean
  createdAt: string
  afgehandeldOp: string | null
  geannuleerdOp: string | null
}

export interface IdeeAuditDTO {
  id: string
  actie: string
  actorId: string
  actorName: string | null
  details: string | null
  createdAt: string
}

/** Backoffice-view: bevat afhandel-metadata + interne notitie + audit-trail. */
export interface IdeeAdminDTO {
  id: string
  type: IdeeType
  titel: string | null
  requesterId: string
  requester: { id: string; name: string | null; email: string } | null
  categorie: IdeeCategorie
  omschrijving: string
  status: IdeeStatus
  // FO-016 — terugkoppeling (zichtbaar voor de indiener) + publiceer-staat.
  terugkoppeling: string | null
  gelezenOp: string | null
  afgehandeldOp: string | null
  geannuleerdOp: string | null
  interneNotitie: string | null
  gepubliceerd: boolean
  gepubliceerdOp: string | null
  gepubliceerdeTitel: string | null
  gepubliceerdeTekst: string | null
  gepubliceerdBerichtId: string | null
  voorstelVervaltOp: string | null
  doorgezetContactRequestId: string | null
  doorgezetOp: string | null
  createdAt: string
  updatedAt: string
  audits: IdeeAuditDTO[]
}

// ─── FO-016 — openbaar voorstel (portal-feed, NAAM-LOOS) ──────────────────────
//
// Het openbare voorstel toont GEEN indiener-naam: die blijft intern bekend bij
// de behandelaar + teamleider (FO §10). Alleen de (geredigeerde) titel/tekst is
// organisatiebreed zichtbaar.

export interface IdeeVoorstelReactiePublicDTO {
  id: string
  inhoud: string
  auteurNaam: string | null
  createdAt: string
}

/** Rij in de openbare voorstellen-feed (portal). */
export interface IdeeVoorstelDTO {
  id: string
  titel: string
  tekst: string
  gepubliceerdOp: string | null
  voorstelVervaltOp: string | null
  aantalReacties: number
}

/** Detail van een openbaar voorstel incl. de goedgekeurde reacties. */
export interface IdeeVoorstelDetailDTO extends IdeeVoorstelDTO {
  reacties: IdeeVoorstelReactiePublicDTO[]
  // Heeft de huidige medewerker al een reactie ingediend (in welke status)?
  eigenReactieStatus: IdeeReactieStatus | null
}

// ─── FO-016 — moderatie-wachtrij (teamleider, backoffice) ─────────────────────

export interface IdeeModeratieReactieDTO {
  id: string
  ideeId: string
  voorstelTitel: string
  inhoud: string
  auteurNaam: string | null
  auteurAfdeling: string | null
  status: IdeeReactieStatus
  createdAt: string
}

// ─── NL-labels (UI) ──────────────────────────────────────────────────────────

// Labels = wat de medewerker ziet (Jelle's lijst). De enum-keys blijven
// intern; alleen deze labels zijn zichtbare copy. VERBETERING toont als
// "Werkwijze" (de algemene/kwaliteit-categorie = default/fallback).
export const IDEE_CATEGORIE_LABELS: Record<IdeeCategorie, string> = {
  BEVEILIGING: 'Veiligheid',
  FUNCTIONALITEIT: 'App-functionaliteit',
  VERBETERING: 'Werkwijze',
  PLANNING: 'Planning',
  DOCUMENTEN: 'Documenten',
  MATERIAAL: 'Materiaal',
}

export const IDEE_TYPE_LABELS: Record<IdeeType, string> = {
  IDEE: 'Idee',
  FEEDBACK: 'Feedback',
  VRAAG: 'Vraag',
}

export const IDEE_STATUS_LABELS: Record<IdeeStatus, string> = {
  OPEN: 'Ingediend',
  GELEZEN: 'Gelezen',
  AFGEHANDELD: 'Afgehandeld',
  GEANNULEERD: 'Ingetrokken',
  IN_BEHANDELING: 'In behandeling',
  OVERGENOMEN: 'Overgenomen',
  AFGEWEZEN: 'Afgewezen',
}

export type IdeeStatusBadgeVariant = 'warning' | 'info' | 'success' | 'secondary' | 'danger'

export const IDEE_STATUS_BADGE_VARIANT: Record<IdeeStatus, IdeeStatusBadgeVariant> = {
  OPEN: 'warning',
  GELEZEN: 'info',
  AFGEHANDELD: 'success',
  GEANNULEERD: 'secondary',
  IN_BEHANDELING: 'info',
  OVERGENOMEN: 'success',
  AFGEWEZEN: 'danger',
}

export const IDEE_REACTIE_STATUS_LABELS: Record<IdeeReactieStatus, string> = {
  IN_MODERATIE: 'Wacht op goedkeuring',
  GOEDGEKEURD: 'Geplaatst',
  AFGEWEZEN: 'Afgewezen',
}
